import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import { useAuth } from '../../../core/providers/AuthProvider';
import { ROLES } from '../../../shared/constant/roles';
import Pagination from '../../../shared/components/Pagination';
import Spinner from '../../../shared/components/Spinner';
import { confirmAction, showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { createTeacher, deleteTeacher, getTeachers, updateTeacher } from '../services/teacherService';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import TeacherFormModal from './TeacherFormModal';

const PAGE_SIZE = 10;

function TeachersPage() {
  // Only super admins add teachers (and manage their logins); teachers can't sign themselves up.
  const { role } = useAuth();
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const [teachers, setTeachers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, count } = await getTeachers({ search, page, pageSize: PAGE_SIZE });
      setTeachers(data);
      setTotalCount(count);
    } catch (err) {
      showAlert({ text: err.message || 'Failed to load teachers' });
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const openCreateForm = () => {
    setEditingTeacher(null);
    setIsFormOpen(true);
  };

  const openEditForm = (teacher) => {
    setEditingTeacher(teacher);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    if (editingTeacher) {
      await updateTeacher(editingTeacher.id, values);
      showSuccessToast('Teacher updated');
    } else {
      await createTeacher(values);
      showSuccessToast('Teacher added');
    }
    setIsFormOpen(false);
    setEditingTeacher(null);
    loadTeachers();
  };

  const handleToggleApproved = async (teacher) => {
    try {
      await updateTeacher(teacher.id, { approved: !teacher.approved });
      showSuccessToast(teacher.approved ? 'Teacher deactivated' : 'Teacher activated');
      loadTeachers();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to update the account status' });
    }
  };

  const handleDelete = async (teacher) => {
    const name = `${teacher.firstname} ${teacher.lastname}`;
    const confirmed = await confirmAction({
      title: 'Remove teacher?',
      text: `${name} and their login will be removed. Subjects assigned to them will be left unassigned.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await deleteTeacher(teacher.id, name);
      showSuccessToast('Teacher removed');
      loadTeachers();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to remove teacher' });
    }
  };

  return (
    <div className="min-h-dvh w-full flex flex-col bg-appbg">
      <AdminHeader />

      <main className="flex-1 w-full p-4 lg:w-4/5 lg:mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Link to="/dashboard" aria-label="Back to dashboard" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-[#0b4a71]">Teachers</h1>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search teachers..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded-md border border-[#4ea6d0] pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>

          {isSuperAdmin && (
            <button
              type="button"
              onClick={openCreateForm}
              className="flex items-center justify-center gap-1.5 rounded-md bg-[#0b4a71] px-3 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              Add Teacher
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <Spinner />
          ) : teachers.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No teachers found.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#4ea6d0] text-left text-white">
                    <tr>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Username</th>
                      <th className="px-4 py-3 font-medium">Advisory</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {teachers.map((teacher) => (
                      <tr key={teacher.id} className="hover:bg-[#4ea6d0]/20">
                        <td className="px-4 py-3 font-medium text-[#0b4a71]">
                          {teacher.firstname} {teacher.middlename ? `${teacher.middlename} ` : ''}{teacher.lastname}
                        </td>
                        {/* Teachers from before usernames still sign in with their email. */}
                        <td className="px-4 py-3 text-gray-600">{teacher.username || teacher.email || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {teacher.advisory
                            ? `${getGradeLevelLabel(teacher.advisory.grade_level)} - ${teacher.advisory.section}`
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {!teacher.user_id ? (
                            <span
                              title="No login yet. Edit the teacher and set a password to create one."
                              className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"
                            >
                              No login
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleToggleApproved(teacher)}
                              title={teacher.approved ? 'Click to deactivate' : 'Click to activate'}
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                teacher.approved
                                  ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                  : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                              }`}
                            >
                              {teacher.approved ? 'Active' : 'Inactive'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditForm(teacher)}
                              aria-label="Edit teacher"
                              className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-[#0b4a71]"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            {isSuperAdmin && (
                            <button
                              type="button"
                              onClick={() => handleDelete(teacher)}
                              aria-label="Remove teacher"
                              className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </main>

      {isFormOpen && (
        <TeacherFormModal
          teacher={editingTeacher}
          canManageLogin={isSuperAdmin}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}

export default TeachersPage;
